import { Button, Grid, MenuItem, TextField } from '@mui/material';
import Alert from '@mui/material/Alert';
import { useSnackbar } from 'notistack';
import React, { ChangeEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';
import ApplicationsService from 'services/api/ApplicationsService';
import useRequest from 'hooks/useRequest';
import {
  defaultGridContainerProps,
  defaultGridItemProps,
  defaultSnackbarErrorProps,
  getInputLoadingProps,
} from 'util/Layout';
import FormFooter from 'components/shared/FormFooter';
import routes from 'store/configs/Routes';
import ApplicationType from 'store/types/ApplicationType';
import Spinner from 'components/shared/Spinner';
import Application from 'store/types/Application';
import ApplicationTypeCard from './ApplicationTypeCard';
import Modal from 'components/shared/Modal';
import { UserContext } from 'components/UserGuard';
import ApplicationStatus from 'store/enums/ApplicationStatus';
import { ParametersContext } from 'components/ParametersGuard';
import { getUserRoles } from 'util/User';
import { getCategoryList, getFilteredAppTypes, getFormattedApplicationStatus } from 'util/Application';

import commonStyles from 'styles/common.module.scss';
import styles from './SelectApplicationTypePage.module.scss';
import CustomerType from 'store/enums/CustomerType';

const MAX_DRAFT_APPLICATIONS = 10;

const isApplicationLimited = ({ id, limit }: ApplicationType, userApplications?: Application[]): boolean =>
  !!limit &&
  !!userApplications &&
  userApplications?.filter(
    ({ typeId, status }) => typeId === id && status !== ApplicationStatus.Draft && status !== ApplicationStatus.Denied
  ).length >= limit;

const getCategoryAppList = (list: ApplicationType[], selectedCategory: string): ApplicationType[] => {
  return list.filter(({ category }) => category.toLowerCase() === selectedCategory.toLowerCase());
};

const SelectApplicationTypePage: React.FunctionComponent = () => {
  const { customerId = '' } = useParams<{ customerId: string }>();
  const { search } = useLocation();
  const [applications, setApplications] = useState<Application[]>([]);
  const applicationTypesRequest = useMemo(
    () =>
      customerId ? () => ApplicationsService.getApplicationTypes(customerId) : ApplicationsService.getApplicationTypes,
    [customerId]
  );
  const { data, loading, error } = useRequest<ApplicationType[]>(applicationTypesRequest);
  const {
    applications: { applicationButtonLabel },
  } = useContext(ParametersContext);
  const applicationsRequest = useMemo(
    () => (customerId ? () => ApplicationsService.getUserApplications(customerId) : undefined),
    [customerId]
  );
  const {
    data: userApplicationsData,
    loading: userApplicationsLoading,
    error: userApplicationsError,
  } = useRequest<Application[]>(applicationsRequest);
  const history = useHistory();
  const { functions } = useContext(UserContext);
  const userRoles: string[] = useMemo(() => getUserRoles(functions), [functions]);
  const { enqueueSnackbar } = useSnackbar();
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [warningModalOpen, setWarningModalOpen] = useState<boolean>(false);
  const [selectedTypeId, setSelectedTypeId] = useState<ApplicationType['id']>('');
  const filteredAppTypes: ApplicationType[] = useMemo(
    () => getFilteredAppTypes(data || [], userRoles, customerId ? CustomerType.Company : CustomerType.Individual),
    [customerId, data, userRoles]
  );
  const categoryList: string[] = useMemo(() => getCategoryList(filteredAppTypes), [filteredAppTypes]);
  const categoryAppList: ApplicationType[] = useMemo(
    () => getCategoryAppList(filteredAppTypes || [], selectedCategory),
    [selectedCategory, filteredAppTypes]
  );

  useEffect(() => {
    if (userApplicationsData?.length) {
      setApplications(
        userApplicationsData.map((application) => ({
          ...application,
          status: getFormattedApplicationStatus(application.status),
        }))
      );
    }
  }, [userApplicationsData]);

  useEffect(() => {
    if (data?.length && categoryList?.length) {
      if (categoryList.length === 1) {
        setSelectedCategory(categoryList[0]);
      }

      // Handle encoding of restricted query string parameters, such as the ampersand.
      const paramsString = search.replace('?category=', '');
      const queryStringEncoded = `?category=${encodeURIComponent(decodeURIComponent(paramsString))}`;
      const params = new URLSearchParams(queryStringEncoded);
      const categoryParam = params.get('category') || '';

      if (categoryParam) {
        const category = categoryList.find((item) => item.toLowerCase() === categoryParam.toLowerCase());
        if (category) {
          setSelectedCategory(category);
          setSelectedTypeId('');
        }
      }
    }
  }, [categoryList, data?.length, search]);

  const handleWarningModalClose = useCallback(() => {
    setWarningModalOpen(false);
  }, []);

  const handleCategoryChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSelectedCategory(e.target.value);
    setSelectedTypeId('');
  }, []);

  const handleTypeClick = useCallback(({ id }: ApplicationType) => {
    setSelectedTypeId(id);
  }, []);

  const handleCancel = useCallback(() => {
    history.goBack();
  }, [history]);

  const handleSubmit = useCallback(() => {
    const draftApplications: Application[] = (applications || []).filter(
      ({ status }) => status === ApplicationStatus.Draft
    );

    if (draftApplications?.length >= MAX_DRAFT_APPLICATIONS) {
      setWarningModalOpen(true);
    } else {
      setSubmitLoading(true);
      ApplicationsService.createApplication(selectedTypeId, '', customerId)
        .then(({ id }: Application) => {
          setSubmitLoading(false);
          enqueueSnackbar('Draft application successfully created', { variant: 'success' });

          history.push(`${routes.editApplication}/${id}`);
        })
        .catch((errorMessage: string) => {
          setSubmitLoading(false);
          enqueueSnackbar(errorMessage, defaultSnackbarErrorProps);
        });
    }
  }, [applications, selectedTypeId, customerId, enqueueSnackbar, history]);

  return (
    <Spinner loading={submitLoading} fullPage={true}>
      <h1 className={commonStyles.pageTitle}>{'New Application'}</h1>
      {error || userApplicationsError ? (
        <Alert severity={'error'} className={commonStyles.alert}>
          {error}
        </Alert>
      ) : (
        <Grid {...defaultGridContainerProps}>
          <Grid {...defaultGridItemProps} sm={5}>
            <h5 className={commonStyles.subTitle}>{'What are you applying for?'}</h5>
            <TextField
              select={true}
              disabled={loading || userApplicationsLoading}
              onChange={handleCategoryChange}
              value={selectedCategory}
              InputProps={getInputLoadingProps(loading)}
            >
              {categoryList.map((name: string, index: number) => (
                <MenuItem value={name} key={`appCategory-${name}-${index}`}>
                  {name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {!!categoryAppList.length && (
            <Grid {...defaultGridItemProps}>
              <h5>{'Select Application'}</h5>
              <Grid {...defaultGridContainerProps}>
                {categoryAppList.map((item: ApplicationType) => (
                  <Grid {...defaultGridItemProps} key={`application-type-${item.id}`}>
                    <ApplicationTypeCard
                      data={item}
                      limited={isApplicationLimited(item, applications)}
                      onClick={handleTypeClick}
                      selected={selectedTypeId === item.id}
                      customFreeButtonLabel={applicationButtonLabel}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}
        </Grid>
      )}
      <FormFooter onSubmit={handleSubmit} submitButtonName={'Next'} submitButtonDisabled={!selectedTypeId}>
        <Button color={'secondary'} variant={'outlined'} onClick={handleCancel} className={styles.cancelButton}>
          {'Cancel'}
        </Button>
      </FormFooter>
      <Modal
        open={warningModalOpen}
        title={'Warning'}
        onClose={handleWarningModalClose}
        maxWidth={'xs'}
        actions={
          <Button color={'secondary'} variant={'outlined'} onClick={handleWarningModalClose}>
            {'Close'}
          </Button>
        }
      >
        <p className={commonStyles.text}>
          {'You already have 10 draft applications, please delete the unnecessary ones to create a new application'}
        </p>
      </Modal>
    </Spinner>
  );
};

export default SelectApplicationTypePage;
